// GovtFeeData.js

export const GovtPageInfo = {
  title: "Government Fee Change History",
  subtitle: "A comprehensive record of government visa and processing fee changes across different countries.",
  lastUpdated: "January 15, 2025",
  disclaimer: "Fees are based on official government sources, with all updates tracked for transparency. Please verify the latest fees before traveling."
};

// Backend should supply dynamic country options via config/API.
export const GovtCountries = [];

// Backend should supply dynamic visa type options via config/API.
export const GovtVisaTypes = [];

// Backend should supply dynamic fee history via config/API.
export const GovtFeeHistory = [];

export const GovtItemsPerPage = 10;

export const GovtSearchConfig = {
  placeholder: "Search by country, visa type, or reason...",
  filterByLabel: "Filter by:",
  allCountriesOption: "All Countries",
  allVisaTypesOption: "All Visa Types",
};

export const GovtEmptyState = {
  title: "No results found",
  hint: "Try adjusting your search or filter criteria",
};

export const GovtTableHeaders = [
  "Country",
  "Visa Type",
  "Effective Date",
  "Fee Change",
  "Change",
  "Reason",
];

export const GovtPaginationConfig = {
  previousText: "Previous",
  nextText: "Next",
  ellipsis: "...",
  maxVisiblePages: 5,
};

export const GovtNoticeInfo = {
  title: "Important Notice",
};

export const GovtFooterInfo = {
  text: "This data is compiled from official government sources and is updated regularly. Fee changes are tracked and documented for transparency purposes.",
};

export const GovtFormatShowingRecords = (start, end, total) =>
  `Showing ${start} - ${end} of ${total} records`;

export const GovtUniqueVisaTypes = [...new Set(GovtFeeHistory.map(item => item.visaType))].sort();

export const GovtGetStatistics = () => {
  const totalChanges = GovtFeeHistory.length;
  const increases = GovtFeeHistory.filter(item => item.changeType === 'increase').length;
  const decreases = GovtFeeHistory.filter(item => item.changeType === 'decrease').length;
  const countriesAffected = new Set(GovtFeeHistory.map(item => item.countryCode)).size;

  return {
    totalChanges,
    increases,
    decreases,
    countriesAffected
  };
};

export const GovtFormatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

export const GovtFormatCurrency = (amount, currency) => {
  if (amount == null) return '—';
  const num = Number(amount);
  if (!isFinite(num)) return '—';

  let symbol = currency;
  try {
    symbol = new Intl.NumberFormat('en', { style: 'currency', currency, minimumFractionDigits: 0 })
      .formatToParts(0).find(p => p.type === 'currency')?.value || currency;
  } catch {}  

  return `${symbol}${num.toFixed(2)}`;
};

export const GovtCalculatePercentage = (oldFee, newFee) => {
  if (oldFee == null || newFee == null) return '—';
  const oldNum = Number(oldFee);
  const newNum = Number(newFee);
  if (oldNum === 0) return '—';
  const change = ((newNum - oldNum) / oldNum) * 100;
  if (!isFinite(change)) return '—';
  return change.toFixed(1);
};

// ----------------------------------------------------------------
// Country Search Aliases — short forms / common names  full names
// ----------------------------------------------------------------
export const COUNTRY_ALIASES = {
  usa: "United States",
  us: "United States",
  america: "United States",
  uk: "United Kingdom",
  gb: "United Kingdom",
  britain: "United Kingdom",
  england: "United Kingdom",
  uae: "United Arab Emirates",
  dubai: "United Arab Emirates",
  emirates: "United Arab Emirates",
  aus: "Australia",
  nz: "New Zealand",
  sg: "Singapore",
  jp: "Japan",
  jpn: "Japan",
  de: "Germany",
  ger: "Germany",
  fr: "France",
  fra: "France",
  ca: "Canada",
  can: "Canada",
  nl: "Netherlands",
  holland: "Netherlands",
  it: "Italy",
  ita: "Italy",
  th: "Thailand",
  thai: "Thailand",
  vn: "Vietnam",
  viet: "Vietnam",
  my: "Malaysia",
  lk: "Sri Lanka",
  in: "India",
  ind: "India",
};

export function matchesCountrySearch(countryName, query) {
  if (!query) return true;
  if (!countryName) return false;
  const q = query.toLowerCase().trim();
  const name = countryName.toLowerCase();
  if (name.includes(q)) return true;
  const expanded = COUNTRY_ALIASES[q];
  if (expanded && name.includes(expanded.toLowerCase())) return true;
  if (q.length >= 2) {
    for (const [alias, full] of Object.entries(COUNTRY_ALIASES)) {
      if (alias.startsWith(q) && name.includes(full.toLowerCase())) return true;
    }
  }
  return false;
}

